import { useLocation } from 'react-router';
import { useAuthentication } from '../useAuthProvider';

export const useRequestToken = () => {
  return useAuthentication(state => state.getRequestToken);
};

const InProgressCache = new Map<string, Promise<unknown>>();

async function withCache<U>(key: string, func: () => Promise<U>): Promise<U> {
  if (InProgressCache.has(key)) {
    const promise = InProgressCache.get(key) as Promise<U>;
    if (!promise) throw new Error('Promise is undefined');
    return promise;
  }
  const action = func();
  InProgressCache.set(key, action);
  return await action.finally(() => {
    InProgressCache.delete(key);
  });
}

export const useSignIn = () => {
  const [signIn, signInSilent] = useAuthentication(state => [state.signIn, state.signInSilent]);
  const location = useLocation();

  return async (from?: string) => {
    try {
      if (location.pathname === '/auth/signin') throw new Error();
      return await withCache('signInSilent', signInSilent);
    } catch (e) {
      return await withCache(`signin-${from}`, () => signIn(from));
    }
  };
};

export const useSignUp = () => {
  const signUp = useAuthentication(state => state.signUp);

  return async () =>
    await withCache('signup', async () => {
      await signUp();
    });
};

export const useSignOut = () => {
  const signOut = useAuthentication(state => state.signOut);
  return async () => await withCache('signout', signOut);
};
